// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { TypographyProps } from "@insite/mobius/Typography";
import { pdpBomViewStyles } from "@stoneage/App/Widgets/ProductDetails/ProductDetailsBomView";
import IconArrowLeftCopper from "@stoneage/XDL/Icons/IconArrowLeftCopper";
import IconTooltip from "@stoneage/XDL/Icons/IconTooltip";
import { color, xScreen } from "@stoneage/XDL/Utilities";
import { layoutContainer } from "@stoneage/XDL/Utilities/SharedStyles/sharedStyles";
import { css } from "styled-components";

pdpBomViewStyles.pointWrapper = {
    css: css`
        display: flex;
        position: absolute;
        top: ${(props: any) => `calc(${props.yPercentage}% - 15px)`};
        left: ${(props: any) => `calc(${props.xPercentage}% - 15px)`};
        width: 30px;
        height: 30px;
        background: ${color("common.ui3")};
        border-radius: 50%;
        ${xScreen("lte", "md")} {
            top: ${(props: any) => `calc(${props.yPercentage}% - 10px)`};
            left: ${(props: any) => `calc(${props.xPercentage}% - 10px)`};
            width: 20px;
            height: 20px;
        }
        ${xScreen("lte", 767)} {
            top: ${(props: any) => `calc(${props.yPercentage}% - 7px)`};
            left: ${(props: any) => `calc(${props.xPercentage}% - 7px)`};
            width: 14px;
            height: 14px;
        }
    `,
};

pdpBomViewStyles.point = {
    css: css`
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background: ${color("primary.main")};
        color: ${color("common.ui3")};
        font-size: 15px;
        font-weight: bold;
        line-height: 18px;
        text-align: center;
        z-index: 2;
        &:hover {
            background: ${color("primary.main")};
        }
        &&&.selected {
            z-index: 1280;
            box-shadow: 0 0 10px ${color("primary.main")};
        }
        opacity: 0.25;

        &.is-in-filteres-group {
            opacity: 1;
            cursor: pointer;
        }
        ${xScreen("lte", "md")} {
            font-size: 10px;
            line-height: 12px;
            width: 20px;
            height: 20px;
        }
        ${xScreen("lte", 767)} {
            font-size: 8px;
            line-height: 10px;
            width: 14px;
            height: 14px;
        }
    `,
};

pdpBomViewStyles.hiddenPoint = {
    css: css`
        position: absolute;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background: transparent;
        ${xScreen("lte", "md")} {
            width: 20px;
            height: 20px;
        }
        ${xScreen("lte", 767)} {
            width: 14px;
            height: 14px;
        }
    `,
};

pdpBomViewStyles.tippyPinWrapper = {
    css: css`
        .tippy-box {
            background: ${color("common.ui4")};
            color: ${color("common.ui1")};
            padding: 17px;
            min-width: 372px;
            max-width: 372px !important;
            box-shadow: 0 2px 20px 0 ${color("common.ui1", 0.66)};
        }
        .tippy-box[data-placement^="top"] > .tippy-arrow {
            bottom: -10px;
            &:before {
                transform: rotate(45deg);
            }
        }

        .tippy-box[data-placement^="bottom"] > .tippy-arrow {
            top: -10px;
            &:before {
                transform: rotate(-135deg);
            }
        }

        .tippy-box[data-placement^="left"] > .tippy-arrow {
            right: -10px;
            &:before {
                transform: rotate(-45deg);
            }
        }

        .tippy-box[data-placement^="right"] > .tippy-arrow {
            left: -10px;
            &:before {
                transform: rotate(135deg);
            }
        }

        .tippy-arrow,
        .tippy-arrow::before {
            position: absolute;
            width: 20px;
            height: 20px;
            z-index: 1;
        }

        .tippy-arrow::before {
            content: "";
            background: ${color("common.ui4")};
            border-color: ${color("common.ui4")};
            border-style: solid;
            border-width: 0 1px 1px 0;
            border-top-left-radius: 0;
            border-top-right-radius: 0;
            border-bottom-right-radius: 4px;
            border-bottom-left-radius: 0px;
        }

        .tippy-content {
            padding: 0;
        }
        & > div[id^="tippy"] {
            z-index 1200 !important;
        }
    `,
};

pdpBomViewStyles.imageWrapper = {
    css: css`
        position: relative;
        margin-bottom: 20px;
        border: 1px solid ${color("common.ui6")};
        overflow: hidden;
        display: flex;
    `,
};

pdpBomViewStyles.backLink = {
    typographyProps: {
        weight: "bold",
        size: 15,
        lineHeight: "18px",
        color: "common.black",
        css: css`
            &&& {
                color: ${color("common.black")};
            }
        `,
    },
    icon: {
        iconProps: {
            src: IconArrowLeftCopper,
            size: 15,
            color: "common.ui1",
            css: css`
                opacity: 0.33;
                margin-right: 8px;
                &,
                svg {
                    width: 8px;
                }
            `,
        },
        position: "left",
    },
    css: css`
        margin-bottom: 20px;
    `,
};

pdpBomViewStyles.filterSectionTitle = {
    variant: "h3",
    forwardAs: "p",
    css: css`
        margin-bottom: 12px;
    `,
};
pdpBomViewStyles.filterName = {
    variant: "h3",
    forwardAs: "p",
    css: css`
        margin-bottom: 12px;
    `,
};

pdpBomViewStyles.kitFilterWrapper = {
    css: css`
        border: 1px solid ${color("common.ui5")};
        padding: 11px 13px;
        min-width: 308px; // kit name might be long
        display: flex;
        align-items: flex-start;
        ${xScreen("lte", "md")} {
            width: calc(50% - 8px);
        }
        ${xScreen("lte", 767)} {
            width: 100%;
        }
        &.isSelected {
            background: ${color("common.ui4")};
        }
    `,
};

pdpBomViewStyles.kitFilter = {
    css: css`
        label {
            font-weight: bold;
            line-height: 18px;
            cursor: pointer;
            &:before {
                min-width: 16px;
            }
        }
        &&&& {
            margin-top: 0;
            display: flex;
        }
    `,
};
pdpBomViewStyles.kitFilters = {
    css: css`
        display: flex;
        flex-direction: row;
        gap: 16px 22px;
        flex-wrap: wrap;
        margin-bottom: 24px;
        & > legend,
        & > span {
            display: none;
        }
        ${xScreen("lte", "md")} {
            column-gap: 16px;
            row-gap: 8px;
        }
    `,
};
pdpBomViewStyles.tooltip = {
    iconProps: {
        src: IconTooltip,
        color: "primary.main",
        size: 16,
        css: css`
            margin-left: 8px;
            &: hover {
                opacity: 0.33;
            }
        `,
    },
    cssOverrides: {
        tooltipClickable: css`
            display: flex;
        `,
    },
};
pdpBomViewStyles.productTitle = {
    variant: "h1",
    css: css`
        margin-bottom: 12px;
    `,
};
pdpBomViewStyles.productPartNumber = {
    variant: "h3",
    forwardAs: "p",
    css: css`
        margin-bottom: 24px;
    `,
};
pdpBomViewStyles.bottomSection = {
    css: css`
        position: sticky;
        bottom: 0;
        z-index: 1286;
        width: 100vw;
        background: ${color("common.ui4")};
        border-top: 1px solid ${color("common.ui6")};
        &.sticky {
            box-shadow: 0 0 25px 0 rgba(79, 79, 119, 0.18);
        }

        ${({ alignX }: any) =>
            alignX === -1
                ? css`
                      visibility: hidden;
                  `
                : css`
                      transform: translateX(-${alignX}px);
                  `}
    `,
};
pdpBomViewStyles.IconInfoWrapper = {
    css: css`
        flex-grow: 1;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        ${xScreen("lte", 767)} {
            display: flex;
            justify-content: space-between;
            width: 100%;
        }
    `,
};
pdpBomViewStyles.InfoWrapper = {
    css: css`
        display: flex;
        justify-content: flex-end;
        column-gap: 24px;
        align-items: baseline;
        ${xScreen("lte", 767)} {
            flex-direction: column;
            row-gap: 8px;
            align-items: flex-end;
        }
    `,
};
pdpBomViewStyles.bottomSectionInnerWrapper = {
    css: css`
        display: flex;
        justify-content: flex-end;

        width: 100%;

        padding: 20px 0;
        column-gap: 24px;
        align-items: baseline;
        ${layoutContainer};

        ${xScreen("lte", "md")} {
            align-items: center;
        }

        ${xScreen("lte", 767)} {
            flex-direction: column;
            row-gap: 8px;
            align-items: flex-end;
        }
    `,
};
pdpBomViewStyles.backToTop = {
    css: css`
        display: none;
        ${xScreen("lte", "md")} {
            display: block;
            margin-right: auto;
        }
    `,
};
const sharedTextStyles: TypographyProps = {
    variant: "p",
    size: 15,
    color: "text",
    weight: "bold",
    css: css`
        letter-spacing: 0;
        line-height: 18px;
        margin-bottom: 0;
        text-transform: none;
    `,
};
pdpBomViewStyles.smallHeadingAndTextStyle = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            width: auto;
        `,
    },
    heading: {
        ...sharedTextStyles,
        css: css`
            ${sharedTextStyles.css}
            display: flex;
            &:after {
                content: ":";
                margin-right: 4px;
            }
        `,
    },
    text: {
        ...sharedTextStyles,
        css: css`
            ${sharedTextStyles.css}
            ${wrapInContainerStyles}
            width: auto;
        `,
    },
};
pdpBomViewStyles.selectPiece = {
    size: 15,
    lineHeight: "26px",
};
pdpBomViewStyles.completeTableWrapper = {
    css: css`
        width: 100%;
        overflow: auto;
        margin-bottom: 24px;
    `,
};
pdpBomViewStyles.tableWrapper = {
    css: css`
        width: 100%;
        overflow: auto;
        margin-bottom: 32px;
    `,
};
pdpBomViewStyles.dataTable = {
    css: css`
        ${xScreen("lte", "md")} {
            &.mobile-view {
                word-wrap: break-word;
                & thead {
                    display: none;
                }
                & td {
                    display: flex;
                    width: 100%;
                    background: transparent;
                    padding: 12px;
                    -moz-box-sizing: border-box;
                    -webkit-box-sizing: border-box;
                    box-sizing: border-box;
                    white-space: break-spaces;
                    height: auto;

                    &:before {
                        content: attr(data-th);
                        font-size: 14px;
                        font-weight: bold;
                        letter-spacing: 0;
                        line-height: 22px;
                        white-space: nowrap;
                        width: 172px;
                    }
                }
                & tbody {
                    display: block;
                    border-top: 8px solid ${color("common.ui7")};

                    @media print {
                        border-top: 2px solid ${color("common.ui7")};
                    }
                }
                & tr {
                    display: block;
                    height: auto;
                    background: ${color("common.white")};
                }
            }
        }
        ${xScreen("lte", 767)} {
            &.mobile-view {
                & td {
                    &:before {
                        width: 152px;
                    }
                }
            }
        }
    `,
};
pdpBomViewStyles.confirmModal = {
    modal: {
        size: 640,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
};
pdpBomViewStyles.buttonWrapper = {
    css: css`
        ${xScreen("lte", 767)} {
            width: 100%;
        }
    `,
};
